import { AnalyticsReleaseStage } from '@cbhq/client-analytics';

export type TargetEnvironment = 'production' | 'staging' | 'development' | 'local';
export type UcpmEnvironment = 'production' | 'staging' | 'development' | 'mock';

type ReleaseStage = 'development' | 'staging' | 'production' | 'local';
type NormalizedReleaseStage = 'development' | 'staging' | 'production';

type WorkspaceEnvironmentConfig = {
  accountsUrl: string;
  workspaceUrl: string;
  host: string;
  apiHost: string;
  retailUrl: string;
  supportUrl: string;
  oauthClientId: string;
  useUSMSignout: boolean;
  unifiedLoginUrl: string;
};

const rawReleaseStage = (process.env.RELEASE_STAGE ||
  process.env.APP_RELEASE_ENVIRONMENT ||
  'development') as ReleaseStage;

const releaseStageMap: Record<ReleaseStage, NormalizedReleaseStage> = {
  production: 'production',
  staging: 'staging',
  development: 'development',
  local: 'development',
};

const releaseStage = releaseStageMap[rawReleaseStage] ?? 'development';

function isInProduction(): boolean {
  return rawReleaseStage === 'production';
}

function isInDevelopment(): boolean {
  return rawReleaseStage === 'development';
}

function isInStaging(): boolean {
  return rawReleaseStage === 'staging';
}

const isValidTargetEnvironment =
  process.env.TARGET_ENV &&
  (['production', 'staging', 'development', 'local'] as const).includes(
    process.env.TARGET_ENV as TargetEnvironment,
  );

const targetEnvironment = (
  isValidTargetEnvironment ? process.env.TARGET_ENV : 'development'
) as TargetEnvironment;

const ucpmEnvironmentMap: Record<TargetEnvironment, UcpmEnvironment> = {
  production: 'production',
  staging: 'staging',
  development: 'development',
  local: 'development',
};

const ucpmEnvironment: UcpmEnvironment = ucpmEnvironmentMap[targetEnvironment];

const oauthClientId = process.env.OAUTH_CLIENT_ID || '';

const ApiEnvironmentMapping: Record<TargetEnvironment, WorkspaceEnvironmentConfig> = {
  production: {
    accountsUrl: 'https://accounts.coinbase.com',
    workspaceUrl: 'https://workspace.coinbase.com',
    host: 'https://workspace.coinbase.com',
    apiHost: '/api/v1',
    retailUrl: 'https://coinbase.com',
    supportUrl: 'https://help.coinbase.com',
    oauthClientId,
    useUSMSignout: false,
    unifiedLoginUrl: 'https://login.coinbase.com',
  },
  staging: {
    accountsUrl: 'https://accounts-staging.cbhq.net',
    workspaceUrl: 'https://workspace-staging.cbhq.net',
    host: 'https://workspace-staging.cbhq.net',
    apiHost: '/api/v1',
    retailUrl: 'https://coinbase-staging.cbhq.net',
    supportUrl: 'https://helpcenter-staging.cbhq.net',
    oauthClientId,
    useUSMSignout: true,
    unifiedLoginUrl: 'https://login-staging.cbhq.net',
  },
  development: {
    accountsUrl: 'https://accounts-dev.cbhq.net',
    workspaceUrl: 'https://workspace-dev.cbhq.net',
    host: 'https://workspace-dev.cbhq.net',
    apiHost: '/api/v1',
    retailUrl: 'https://coinbase-dev.cbhq.net',
    supportUrl: 'https://helpcenter-dev.cbhq.net',
    oauthClientId,
    useUSMSignout: true,
    unifiedLoginUrl: 'https://login-dev.cbhq.net',
  },
  local: {
    accountsUrl: 'https://accounts-dev.cbhq.net',
    workspaceUrl: 'https://workspace-local.cbhq.net:8081',
    host: 'https://workspace-local.cbhq.net:8081',
    apiHost: '/api/v1',
    retailUrl: 'https://coinbase-dev.cbhq.net',
    supportUrl: 'https://helpcenter-dev.cbhq.net',
    oauthClientId,
    useUSMSignout: true,
    unifiedLoginUrl: 'https://login-dev.cbhq.net',
  },
};

const {
  accountsUrl,
  workspaceUrl,
  host,
  apiHost,
  retailUrl,
  supportUrl,
  oauthClientId: environmentOauthClientId,
  useUSMSignout,
  unifiedLoginUrl,
} = ApiEnvironmentMapping[targetEnvironment];

const shouldMockdata = process.env.API_ENV === 'mock';

const analyticsReleaseStage = {
  production: AnalyticsReleaseStage?.production ?? 'production',
  development: AnalyticsReleaseStage?.development ?? 'development',
};

const analyticsConfig = {
  isProd: targetEnvironment === 'production',
  showDebugLogging: targetEnvironment !== 'production',
  releaseStage: isInProduction()
    ? analyticsReleaseStage.production
    : analyticsReleaseStage.development,
};

const analyticsEnvironment = process.env.ANALYTICS_ENVIRONMENT;

const sentryDsn = process.env.COINBASE_PUBLIC_SENTRY_DSN || '';

const appVersion = process.env.APP_VERSION ?? 'local';

const projectId = '39XQ6jVphVmFo6lBhzsJU8YgWvg';

export {
  accountsUrl,
  analyticsConfig,
  analyticsEnvironment,
  ApiEnvironmentMapping,
  apiHost,
  appVersion,
  host,
  isInDevelopment,
  isInProduction,
  isInStaging,
  environmentOauthClientId as oauthClientId,
  projectId,
  releaseStage,
  retailUrl,
  sentryDsn,
  shouldMockdata,
  supportUrl,
  targetEnvironment,
  ucpmEnvironment,
  unifiedLoginUrl,
  useUSMSignout,
  workspaceUrl,
};
